import { supabase } from "@/lib/supabase";
import type { SpaceTemplate, InsertSpaceTemplate, SpaceTemplateData } from "@shared/schema";
import { FLAGSHIP_TEMPLATES, type FlagshipTemplate } from "@/data/spaceTemplates";

export interface CreateSpaceFromTemplateResult {
  spaceId: string;
  spaceName: string;
  topicsCreated: number;
  agentsCreated: number;
  promptsCreated: number;
}

class SpaceTemplatesService {
  async getAllTemplates(userId?: string): Promise<SpaceTemplate[]> {
    let query = supabase
      .from("space_templates")
      .select("id, slug, name, description, tagline, icon, color, category, tier, template_data, is_system, is_featured, is_published, created_by_user_id, usage_count, version, created_at, updated_at")
      .eq("is_published", "true");
    
    if (userId) {
      query = query.or(`is_system.eq.true,created_by_user_id.eq.${userId}`);
    } else {
      query = query.eq("is_system", "true");
    }
    
    const { data: dbTemplates, error } = await query
      .order("is_featured", { ascending: false })
      .order("usage_count", { ascending: false });

    if (error) {
      console.error("Error fetching templates from DB:", error);
    }

    const dbTemplatesList = dbTemplates || [];
    const existingSlugs = new Set(dbTemplatesList.map((t: SpaceTemplate) => t.slug));
    const flagshipAsDbFormat: SpaceTemplate[] = FLAGSHIP_TEMPLATES
      .filter((t: FlagshipTemplate) => !existingSlugs.has(t.slug))
      .map((t: FlagshipTemplate) => this.convertFlagshipToDbFormat(t));

    return [...flagshipAsDbFormat, ...dbTemplatesList];
  }

  async getPublicTemplates(): Promise<SpaceTemplate[]> {
    const { data: dbTemplates, error } = await supabase
      .from("space_templates")
      .select("id, slug, name, description, tagline, icon, color, category, tier, template_data, is_system, is_featured, is_published, created_by_user_id, usage_count, version, created_at, updated_at")
      .eq("is_published", "true")
      .order("is_featured", { ascending: false })
      .order("usage_count", { ascending: false });

    if (error) {
      console.error("Error fetching public templates:", error);
    }

    const dbTemplatesList = dbTemplates || [];
    const existingSlugs = new Set(dbTemplatesList.map((t: SpaceTemplate) => t.slug));
    const flagshipAsDbFormat: SpaceTemplate[] = FLAGSHIP_TEMPLATES
      .filter((t: FlagshipTemplate) => !existingSlugs.has(t.slug))
      .map((t: FlagshipTemplate) => this.convertFlagshipToDbFormat(t));

    return [...flagshipAsDbFormat, ...dbTemplatesList];
  }

  async getTemplateBySlug(slug: string): Promise<SpaceTemplate | null> {
    const { data, error } = await supabase
      .from("space_templates")
      .select("id, slug, name, description, tagline, icon, color, category, tier, template_data, is_system, is_featured, is_published, created_by_user_id, usage_count, version, created_at, updated_at")
      .eq("slug", slug)
      .single();

    if (error || !data) {
      const flagship = FLAGSHIP_TEMPLATES.find(t => t.slug === slug);
      if (flagship) {
        return this.convertFlagshipToDbFormat(flagship);
      }
      return null;
    }

    return data;
  }

  async getTemplateById(id: string): Promise<SpaceTemplate | null> {
    if (id.startsWith("flagship-")) {
      const slug = id.replace("flagship-", "");
      const flagship = FLAGSHIP_TEMPLATES.find(t => t.slug === slug);
      if (flagship) {
        return this.convertFlagshipToDbFormat(flagship);
      }
    }

    const { data, error } = await supabase
      .from("space_templates")
      .select("id, slug, name, description, tagline, icon, color, category, tier, template_data, is_system, is_featured, is_published, created_by_user_id, usage_count, version, created_at, updated_at")
      .eq("id", id)
      .single();

    if (error) {
      console.error("Error fetching template:", error);
      return null;
    }

    return data;
  }

  async createTemplate(template: InsertSpaceTemplate): Promise<SpaceTemplate | null> {
    const { data, error } = await supabase
      .from("space_templates")
      .insert(template)
      .select()
      .single();

    if (error) {
      console.error("Error creating template:", error);
      throw new Error(error.message);
    }

    return data;
  }

  async updateTemplate(id: string, updates: Partial<InsertSpaceTemplate>): Promise<SpaceTemplate | null> {
    const { data, error } = await supabase
      .from("space_templates")
      .update({ ...updates, updatedAt: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating template:", error);
      throw new Error(error.message);
    }

    return data;
  }

  async deleteTemplate(id: string): Promise<boolean> {
    const { error } = await supabase
      .from("space_templates")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting template:", error);
      return false;
    }

    return true;
  }

  async createSpaceFromTemplate(
    templateId: string,
    userId: string,
    customName?: string
  ): Promise<CreateSpaceFromTemplateResult> {
    const template = await this.getTemplateById(templateId);
    if (!template) {
      throw new Error("Template not found");
    }

    const templateData = template.templateData as SpaceTemplateData;
    const spaceName = customName || template.name.replace("DEEPLY SMART ", "");

    const { data: space, error: spaceError } = await supabase
      .from("mastermind_spaces")
      .insert({
        name: spaceName,
        description: template.description,
        icon: template.icon,
        color: template.color,
        goals: templateData.goals || [],
        agents: templateData.agents || [],
      })
      .select()
      .single();

    if (spaceError || !space) {
      throw new Error(spaceError?.message || "Failed to create space");
    }

    const { error: userSpaceError } = await supabase
      .from("user_spaces")
      .insert({
        user_id: userId,
        space_id: space.id,
        role: "owner",
      });

    if (userSpaceError) {
      console.error("Error linking user to space:", userSpaceError);
    }

    let topicsCreated = 0;
    if (templateData.topics && templateData.topics.length > 0) {
      const topicsToInsert = templateData.topics.map((topic, index) => ({
        name: topic.name,
        description: topic.description || "",
        color: topic.color || template.color,
        space_id: space.id,
        order: String(index),
      }));

      const { error: topicsError } = await supabase
        .from("topics")
        .insert(topicsToInsert);

      if (!topicsError) {
        topicsCreated = topicsToInsert.length;
      }
    }

    let promptsCreated = 0;
    if (templateData.prompts && templateData.prompts.length > 0) {
      const promptsToInsert = templateData.prompts.map(prompt => ({
        user_id: userId,
        name: prompt.name,
        description: prompt.description || "",
        prompt_template: prompt.template,
        category: prompt.category || "General",
        icon: prompt.icon || "Sparkles",
        variables: prompt.variables || [],
      }));

      const { error: promptsError } = await supabase
        .from("super_prompts")
        .insert(promptsToInsert);

      if (!promptsError) {
        promptsCreated = promptsToInsert.length;
      }
    }

    await supabase
      .from("space_templates")
      .update({ usage_count: String(parseInt(template.usageCount || "0") + 1) })
      .eq("id", template.id);

    return {
      spaceId: space.id,
      spaceName: space.name,
      topicsCreated,
      agentsCreated: templateData.agents?.length || 0,
      promptsCreated,
    };
  }

  async getUserTemplates(userId: string): Promise<SpaceTemplate[]> {
    const { data, error } = await supabase
      .from("space_templates")
      .select("id, slug, name, description, tagline, icon, color, category, tier, template_data, is_system, is_featured, is_published, created_by_user_id, usage_count, version, created_at, updated_at")
      .eq("created_by_user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching user templates:", error);
      return [];
    }

    return data || [];
  }

  private convertFlagshipToDbFormat(flagship: FlagshipTemplate): SpaceTemplate {
    return {
      id: `flagship-${flagship.slug}`,
      slug: flagship.slug,
      name: flagship.name,
      description: flagship.description,
      tagline: flagship.tagline,
      icon: flagship.icon,
      color: flagship.color,
      category: flagship.category,
      tier: flagship.tier,
      templateData: flagship.templateData,
      isSystem: "true",
      isFeatured: flagship.isFeatured ? "true" : "false",
      isPublished: "true",
      createdByUserId: null,
      usageCount: "0",
      version: "1.0.0",
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }
}

export const spaceTemplatesService = new SpaceTemplatesService();
